import {
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { getQuiz } from './quizService';
import { isValidParticipantName } from '../utils/validators';
import { calculatePoints } from '../utils/scoreCalculator';

/**
 * Participant Service
 * Handles all participant-related operations
 */

/**
 * Adds a participant to an active session
 * @param {string} sessionId - Session ID (room code)
 * @param {string} participantName - Participant's display name
 * @returns {Promise<Object>} Participant data with ID
 */
export const joinSession = async (sessionId, participantName) => {
  try {
    // Validate participant name
    if (!isValidParticipantName(participantName)) {
      throw new Error('Ugyldig namn. Må vere 2-20 teikn.');
    }

    // Validate room code format (basic check)
    if (!sessionId || typeof sessionId !== 'string' || sessionId.length !== 6) {
      throw new Error('Ugyldig PIN-kode');
    }

    // Fetch session to verify it exists
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje økta. Sjekk PIN-koden.');
    }

    const sessionData = sessionSnap.data();

    // Check if session is in valid state to join (waiting or active)
    if (sessionData.status === 'finished') {
      throw new Error('Denne økta er allereie avslutta');
    }

    // Check if a participant with the same name already exists (rejoin support)
    const currentParticipants = sessionData.participants || {};
    const existingParticipant = Object.values(currentParticipants).find(
      p => p.name.toLowerCase() === participantName.trim().toLowerCase()
    );

    if (existingParticipant) {
      // Return existing participant instead of creating duplicate
      return existingParticipant;
    }

    // Generate unique participant ID
    const participantId = `p${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

    // Create participant object
    const participant = {
      id: participantId,
      name: participantName.trim(),
      score: 0,
      answers: {},
      joinedAt: new Date().toISOString()
    };

    // Add participant to session
    await updateDoc(sessionRef, {
      [`participants.${participantId}`]: participant
    });

    return participant;
  } catch (error) {
    console.error('Error joining session:', error);
    throw new Error(`Klarte ikkje bli med i økt: ${error.message}`);
  }
};

/**
 * Submits an answer for a participant
 * Calculates points based on correctness and speed
 * @param {string} sessionId - Session ID (room code)
 * @param {string} participantId - Participant's ID
 * @param {string} questionId - Question ID (e.g., "q1")
 * @param {string} answerId - Selected answer ID (e.g., "a2")
 * @returns {Promise<Object>} Result with points earned and correctness
 */
export const submitAnswer = async (sessionId, participantId, questionId, answerId) => {
  try {
    // Validate inputs
    if (!sessionId || !participantId || !questionId || !answerId) {
      throw new Error('Manglar påkravde parametrar');
    }

    // Fetch session
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje økta');
    }

    const sessionData = sessionSnap.data();

    // Verify session is active
    if (sessionData.status !== 'active') {
      throw new Error('Økta er ikkje aktiv');
    }

    // Verify participant exists in session
    if (!sessionData.participants || !sessionData.participants[participantId]) {
      throw new Error('Fann ikkje deltakaren i økta');
    }

    const participant = sessionData.participants[participantId];

    // Check if participant has already answered this question
    if (participant.answers && participant.answers[questionId]) {
      throw new Error('Svar er allereie sendt for dette spørsmålet');
    }

    // Fetch quiz to get correct answer
    const quiz = await getQuiz(sessionData.quizId);
    const question = quiz.questions.find(q => q.id === questionId);

    if (!question) {
      throw new Error('Fann ikkje spørsmålet i kvissen');
    }

    // Find the selected answer and check if it's correct
    const selectedAnswer = question.answers.find(a => a.id === answerId);
    if (!selectedAnswer) {
      throw new Error('Ugyldig svar-ID');
    }

    const isCorrect = selectedAnswer.isCorrect;

    // Calculate time elapsed (in seconds)
    const questionStartTime = new Date(sessionData.questionStartTime);
    const currentTime = new Date();
    const timeElapsed = (currentTime - questionStartTime) / 1000; // Convert to seconds

    // Get time limit from question (default 30s)
    const timeLimit = question.timeLimit || 30;

    // Calculate points earned
    const pointsEarned = calculatePoints(isCorrect, timeElapsed, timeLimit);

    // Update participant's score and answers
    const newScore = participant.score + pointsEarned;

    await updateDoc(sessionRef, {
      [`participants.${participantId}.score`]: newScore,
      [`participants.${participantId}.answers.${questionId}`]: {
        answerId: answerId,
        isCorrect: isCorrect,
        timeElapsed: timeElapsed,
        pointsEarned: pointsEarned,
        submittedAt: new Date().toISOString()
      }
    });

    return {
      isCorrect,
      pointsEarned,
      newScore,
      timeElapsed
    };
  } catch (error) {
    console.error('Error submitting answer:', error);
    throw new Error(`Klarte ikkje sende svar: ${error.message}`);
  }
};
